import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { usePlatformAdmin } from '../admin/usePlatformAdmin';

/**
 * Dev Mode: a platform-admin-only in-app feature-verification walkthrough.
 *
 * State is backed by localStorage (`pc.devMode`) so it survives reloads, but
 * `enabled` is only ever *effective* when the signed-in user is a platform
 * admin (`usePlatformAdmin().data === true`). A non-admin who somehow carries
 * the flag (shared machine, copied storage) sees nothing. When admin status
 * resolves to false we also clear the persisted flag.
 */
const STORAGE_KEY = 'pc.devMode';

interface DevModeContextValue {
  /** True only when enabled AND the user is a confirmed platform admin. */
  enabled: boolean;
  /** Whether the current user is a platform admin (gate for the entry point). */
  isPlatformAdmin: boolean;
  enable: () => void;
  exit: () => void;
}

const DevModeContext = createContext<DevModeContextValue | null>(null);

function readStored(): boolean {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

export function DevModeProvider({ children }: { children: ReactNode }) {
  const adminQ = usePlatformAdmin();
  const isPlatformAdmin = adminQ.data === true;
  const [stored, setStored] = useState<boolean>(() => readStored());

  // If admin status is definitively false, drop any lingering flag so the
  // banner/dock never render for a non-admin.
  useEffect(() => {
    if (adminQ.isSuccess && adminQ.data === false && stored) {
      setStored(false);
      try {
        window.localStorage.removeItem(STORAGE_KEY);
      } catch {
        // ignore storage failures
      }
    }
  }, [adminQ.isSuccess, adminQ.data, stored]);

  const enable = useCallback(() => {
    setStored(true);
    try {
      window.localStorage.setItem(STORAGE_KEY, '1');
    } catch {
      // ignore storage failures
    }
  }, []);

  const exit = useCallback(() => {
    setStored(false);
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch {
      // ignore storage failures
    }
  }, []);

  const value = useMemo<DevModeContextValue>(
    () => ({
      enabled: stored && isPlatformAdmin,
      isPlatformAdmin,
      enable,
      exit,
    }),
    [stored, isPlatformAdmin, enable, exit],
  );

  return <DevModeContext.Provider value={value}>{children}</DevModeContext.Provider>;
}

export function useDevMode(): DevModeContextValue {
  const ctx = useContext(DevModeContext);
  if (!ctx) {
    // Safe fallback so a component that renders outside the provider (e.g. a
    // public route) never crashes — Dev Mode is simply off there.
    return { enabled: false, isPlatformAdmin: false, enable: () => {}, exit: () => {} };
  }
  return ctx;
}
